import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, HeartHandshake, HelpCircle, Wind, Apple, Play } from "lucide-react";
import { BetaForm } from "../components/BetaForm";
import {
  HubPage, Container, SectionHead, Reveal, CategoryCard, MomentCard, ResourceCard,
  CommunityCTA, SearchBox, NewsletterSignup, Seo,
} from "../components/hub/HubKit";
import {
  CATEGORIES, TODAY, LIFE_MOMENTS, COMMUNITY_PROMPTS, FINDS, resourcesByType, RESOURCES,
} from "../data/hub";

const TodayCard = ({ icon: Icon, label, text, testid }) => (
  <div className="flex h-full flex-col rounded-[1.4rem] border border-line bg-white/70 p-6" data-testid={testid}>
    <span className="flex items-center gap-2 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-sage">
      <Icon size={15} strokeWidth={1.9} className="text-sage-deep" /> {label}
    </span>
    <p className="mt-3 serif-display text-lg leading-snug text-charcoal">{text}</p>
  </div>
);

export default function Home() {
  const navigate = useNavigate();
  const onSearch = (e) => {
    e.preventDefault();
    const q = new FormData(e.currentTarget).get("q");
    navigate(`/explore?q=${encodeURIComponent(q || "")}`);
  };
  const learning = resourcesByType("learning");
  const featured = RESOURCES.slice(0, 3);

  return (
    <HubPage testid="home-hub" topClass="pt-20 sm:pt-24">
      <Seo
        title="HEARD — Community & Everyday-Life Resources"
        description="HEARD is a community and everyday-life resource for the questions, changes, wins, and ordinary moments that make us human. Explore resources and join the beta."
      />
      {/* 1. Hero — immersive, full-bleed community photograph with a warm cream reading scrim */}
      <section className="relative overflow-hidden border-b border-line/60 bg-cream" data-testid="home-hero">
        {/* Full-bleed warm community photograph */}
        <img
          src="/hero-community.jpg?v=2"
          alt="A warm, diverse HEARD community of people of different ages, ethnicities and abilities gathered in conversation, sharing support and belonging"
          fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover object-center"
          style={{ filter: "contrast(1.05) saturate(1.05)" }}
          data-testid="hero-devices-image"
        />
        {/* Reading scrims — keep left copy readable while people stay visible on the right */}
        <div className="absolute inset-0 mix-blend-multiply bg-gradient-to-br from-[#3a2a15]/6 via-transparent to-[#2f4b39]/8" />
        <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/78 to-cream/15 lg:from-cream/96 lg:via-cream/55 lg:to-transparent" />
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-cream/80 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-cream/55 to-transparent" />

        <Container className="relative z-10">
          <div className="max-w-2xl py-16 sm:py-24 lg:py-32">
            <Reveal>
              <span className="text-[0.7rem] font-medium uppercase tracking-[0.28em] text-sage-deep">Community · Everyday Resources · Hope</span>
              <h1 className="serif-display mt-4 text-[2.4rem] sm:text-[3.2rem] lg:text-[4rem] font-normal leading-[1.06]">
                <span className="text-charcoal">Life happens.</span>{" "}
                <span className="italic" style={{ color: "#a37b45" }}>You don't have to carry it alone.</span>
              </h1>
              <p className="mt-5 max-w-lg text-base sm:text-lg leading-relaxed text-ink/80">
                HEARD is a community and everyday-life resource for the questions, changes, wins, challenges, and ordinary
                moments that make us human.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/resources" data-testid="hero-explore-resources" className="inline-flex items-center gap-2 rounded-full bg-sage-deep px-7 py-3.5 text-sm font-medium uppercase tracking-[0.16em] text-cream shadow-[0_12px_30px_rgba(47,75,57,0.24)] transition-all duration-300 hover:bg-sage hover:scale-[1.02]">
                  Explore Resources <ArrowRight size={15} />
                </Link>
                <Link to="/community" data-testid="hero-join-community" className="inline-flex items-center gap-2 rounded-full border border-sage-deep bg-cream/60 px-7 py-3.5 text-sm font-medium uppercase tracking-[0.16em] text-sage-deep backdrop-blur-sm transition-colors duration-300 hover:bg-sage-deep hover:text-cream">
                  Join the Community
                </Link>
              </div>
              <div className="mt-7 max-w-lg"><SearchBox onSubmit={onSearch} /></div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* 2. Today at HEARD */}
      <section className="border-b border-line/60 bg-cream">
        <Container className="py-14 sm:py-20">
          <SectionHead overline="Today at HEARD" title="A little support for your everyday." />
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <TodayCard icon={Sparkles} label="Today's affirmation" text={TODAY.affirmation} testid="today-affirmation" />
            <TodayCard icon={HeartHandshake} label="Small kindness" text={TODAY.kindness} testid="today-kindness" />
            <TodayCard icon={HelpCircle} label="Question of the day" text={TODAY.question} testid="today-question" />
            <TodayCard icon={Wind} label="Gentle practice" text={TODAY.practice} testid="today-practice" />
          </div>
        </Container>
      </section>

      {/* 3. What do you need today — 8 category cards */}
      <section className="bg-cream">
        <Container className="py-16 sm:py-24">
          <SectionHead overline="Resource Categories" title="What do you need today?" sub="Practical, gentle guidance for the areas of life we all move through." />
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 4) * 0.05}><CategoryCard cat={c} /></Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* 4. What We're Learning */}
      <section className="border-y border-line/60 bg-cream-deep/40">
        <Container className="py-16 sm:py-24">
          <SectionHead overline="What We're Learning" title="We research. We listen. We share what may help." />
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {learning.map((r, i) => (
              <Reveal key={r.slug} delay={(i % 3) * 0.05}><ResourceCard resource={r} /></Reveal>
            ))}
          </div>
          <Link to="/learning" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-sage-deep hover:text-sage" data-testid="see-all-learning">
            See all research summaries <ArrowRight size={14} />
          </Link>
        </Container>
      </section>

      {/* 5. Everyday Resources */}
      <section className="bg-cream">
        <Container className="py-16 sm:py-24">
          <SectionHead overline="Everyday Resources" title="Featured practical guides." sub="Short, warm, and useful — for real life, not perfect life." />
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((r, i) => (
              <Reveal key={r.slug} delay={(i % 3) * 0.05}><ResourceCard resource={r} /></Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* 6. HEARD Finds */}
      <section className="border-y border-line/60 bg-cream-deep/40">
        <Container className="py-16 sm:py-24">
          <SectionHead overline="HEARD Finds" title="A few things we think are worth a look." />
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {FINDS.slice(0, 3).map((f) => (
              <div key={f.title} className="rounded-[1.4rem] border border-line bg-white/70 p-6" data-testid="find-card">
                <span className="text-[0.6rem] font-semibold uppercase tracking-[0.16em] text-sage-deep">{f.tag}</span>
                <h3 className="mt-3 font-serif text-lg font-semibold text-heading">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/70">{f.note}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 max-w-2xl text-sm leading-relaxed text-ink/60" data-testid="finds-disclosure">
            We'll always tell you when something is sponsored or when HEARD may earn from a recommendation.
          </p>
        </Container>
      </section>

      {/* 7. From the Community */}
      <section className="bg-cream">
        <Container className="py-16 sm:py-24">
          <div className="grid gap-10 lg:grid-cols-[1.3fr_1fr] lg:items-center">
            <div>
              <SectionHead overline="From the Community" title="Conversations worth having." sub="A glimpse of the kinds of prompts our members explore together — shared safely, never private content." />
              <ul className="mt-8 space-y-3">
                {COMMUNITY_PROMPTS.map((p) => (
                  <li key={p} className="rounded-2xl border border-line bg-white/70 px-5 py-4 text-sm text-ink/80" data-testid="community-prompt">
                    "{p}"
                  </li>
                ))}
              </ul>
            </div>
            <CommunityCTA />
          </div>
        </Container>
      </section>

      {/* 7b. A little HEARD in your inbox — newsletter (separate from beta/early access) */}
      <section className="border-y border-line/60 bg-cream-deep/40">
        <Container className="py-16 sm:py-24">
          <NewsletterSignup source="home" />
        </Container>
      </section>

      {/* 8. Explore by life moment */}
      <section className="border-y border-line/60 bg-cream-deep/40">
        <Container className="py-16 sm:py-24">
          <SectionHead overline="Explore by Life Moment" title="Where are you right now?" />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {LIFE_MOMENTS.map((m, i) => (
              <Reveal key={m.label} delay={(i % 4) * 0.04}><MomentCard moment={m} /></Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* 9. Email signup */}
      <section id="get-app" className="bg-cream">
        <Container className="py-16 sm:py-24">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <SectionHead overline="Early Access" title="Be first to shape HEARD." sub="Join the founding community and get early access to the HEARD app as we open it, in phases — never spam." />
              <div className="mt-8 flex flex-col gap-2.5" data-testid="get-app-badges">
                <div className="flex items-center gap-3 rounded-xl border border-line bg-white/60 px-4 py-2.5 opacity-80" title="Coming soon">
                  <Apple size={20} className="text-sage-deep" />
                  <div className="leading-tight"><p className="text-[0.6rem] uppercase tracking-[0.14em] text-ink/50">Coming soon</p><p className="text-sm font-medium text-charcoal">App Store</p></div>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-line bg-white/60 px-4 py-2.5 opacity-80" title="Coming soon">
                  <Play size={20} className="text-sage-deep" />
                  <div className="leading-tight"><p className="text-[0.6rem] uppercase tracking-[0.14em] text-ink/50">Coming soon</p><p className="text-sm font-medium text-charcoal">Google Play</p></div>
                </div>
              </div>
            </div>
            <div id="beta" className="scroll-mt-28"><BetaForm /></div>
          </div>
        </Container>
      </section>
    </HubPage>
  );
}
